/** Field view: canvas heatmap of V or |E| with mask, outline overlay, colorbar. */
import { useEffect, useRef } from "preact/hooks";
import { VIRIDIS_LUT, viridisCss } from "../lib/colormap";
import { siFormat } from "../lib/format";
import { fieldQuantity, fieldRes, showMesh, solveState, view } from "../state/store";
import type { FieldGridMsg, OutlineMsg } from "../solver/protocol";
import { Select } from "./common";

function drawHeatmap(canvas: HTMLCanvasElement, g: FieldGridMsg, quantity: "V" | "E"): { lo: number; hi: number } {
  const { nx, ny } = g;
  canvas.width = nx;
  canvas.height = ny;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(nx, ny);
  const n = nx * ny;
  const val = new Float64Array(n);
  if (quantity === "E" && g.Ex && g.Ey) {
    for (let i = 0; i < n; i++) val[i] = Math.hypot(g.Ex[i]!, g.Ey[i]!);
  } else {
    val.set(g.V);
  }
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < n; i++) {
    if (g.mask[i] !== 0) continue;
    const v = val[i]!;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!(hi > lo)) {
    lo = 0;
    hi = 1;
  }
  const scale = 255 / (hi - lo);
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const src = j * nx + i;
      // canvas rows top-down; grid rows bottom-up (y0 at j=0)
      const dst = ((ny - 1 - j) * nx + i) * 4;
      const m = g.mask[src]!;
      if (m === 2) {
        img.data[dst + 3] = 0; // outside: transparent
      } else if (m === 1) {
        img.data[dst] = 158; // conductor: neutral gray
        img.data[dst + 1] = 158;
        img.data[dst + 2] = 158;
        img.data[dst + 3] = 255;
      } else {
        const t = Math.max(0, Math.min(255, Math.round((val[src]! - lo) * scale)));
        img.data[dst] = VIRIDIS_LUT[t * 3]!;
        img.data[dst + 1] = VIRIDIS_LUT[t * 3 + 1]!;
        img.data[dst + 2] = VIRIDIS_LUT[t * 3 + 2]!;
        img.data[dst + 3] = 255;
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  return { lo, hi };
}

function outlinePath(o: OutlineMsg): string {
  const n = o.xy.length / 2;
  if (n === 0) return "";
  let d = `M ${o.xy[0]} ${o.xy[1]}`;
  const edges = o.closed ? n : n - 1;
  for (let e = 0; e < edges; e++) {
    const j = (e + 1) % n;
    const x = o.xy[2 * j]!;
    const y = o.xy[2 * j + 1]!;
    const r = o.edgeCurve ? o.edgeCurve[3 * e + 2]! : 0;
    if (r > 0) {
      // Pick the sweep flag so the arc bulges away from the chord toward the true circle.
      const cx = o.edgeCurve![3 * e]!;
      const cy = o.edgeCurve![3 * e + 1]!;
      const x0 = o.xy[2 * e]!;
      const y0 = o.xy[2 * e + 1]!;
      const cross = (x0 - cx) * (y - cy) - (y0 - cy) * (x - cx);
      d += ` A ${r} ${r} 0 0 ${cross > 0 ? 1 : 0} ${x} ${y}`;
    } else {
      d += ` L ${x} ${y}`;
    }
  }
  if (o.closed) d += " Z";
  return d;
}

const OUTLINE_COLOR: Record<number, string> = { 1: "#d32f2f", 2: "#616161", 3: "#ffffff" };

export function FieldView() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rangeRef = useRef<{ lo: number; hi: number }>({ lo: 0, hi: 1 });
  const st = solveState.value;
  const g = st.result?.field;
  const quantity = fieldQuantity.value;

  useEffect(() => {
    if (g && canvasRef.current) {
      rangeRef.current = drawHeatmap(canvasRef.current, g, quantity);
    }
  }, [g, quantity]);

  const outlines = st.result?.outlines;
  const mesh = st.result?.mesh;
  const range = rangeRef.current;

  return (
    <div class="field-view">
      <div class="field-controls">
        <Select
          label="Quantity"
          value={quantity}
          options={[
            { value: "V", label: "Potential V" },
            { value: "E", label: "|E| field" },
          ]}
          onChange={(q) => (fieldQuantity.value = q)}
        />
        <Select
          label="Grid"
          value={String(fieldRes.value) as "128" | "256" | "512"}
          options={[
            { value: "128", label: "128²" },
            { value: "256", label: "256²" },
            { value: "512", label: "512²" },
          ]}
          onChange={(r) => (fieldRes.value = Number(r) as 128 | 256 | 512)}
        />
        <label class="field checkbox-field">
          <input type="checkbox" checked={showMesh.value} onChange={(e) => (showMesh.value = (e.target as HTMLInputElement).checked)} />
          <span>Mesh</span>
        </label>
      </div>

      {!g && view.value === "field" && (
        <div class="status-line">{st.status === "solving" ? "Solving…" : "The field is sampled on the next solve."}</div>
      )}

      {g && (
        <div class="field-stage">
          <div class="field-canvas-wrap">
            <canvas ref={canvasRef} class="field-canvas" />
            <svg class="field-overlay" viewBox={`${g.x0} ${-g.y1} ${g.x1 - g.x0} ${g.y1 - g.y0}`} preserveAspectRatio="none">
              <g transform="scale(1,-1)">
                {outlines?.map((o, i) => (
                  <path
                    key={i}
                    d={outlinePath(o)}
                    fill="none"
                    stroke={OUTLINE_COLOR[o.marker] ?? "#fff"}
                    stroke-width={(g.x1 - g.x0) / 300}
                    stroke-dasharray={o.marker === 3 ? `${(g.x1 - g.x0) / 80} ${(g.x1 - g.x0) / 120}` : undefined}
                  />
                ))}
                {mesh && mesh.kind === "triangles" && mesh.triangles && (
                  <path d={meshPath(mesh.xy, mesh.triangles)} fill="none" stroke="rgba(255,255,255,0.35)" stroke-width={(g.x1 - g.x0) / 900} />
                )}
              </g>
            </svg>
          </div>
          <div class="colorbar">
            <div class="colorbar-gradient" style={{ background: `linear-gradient(to top, ${[0, 0.25, 0.5, 0.75, 1].map((t) => viridisCss(t)).join(",")})` }} />
            <div class="colorbar-labels">
              <span>{quantity === "V" ? range.hi.toFixed(2) + " V" : siFormat(range.hi, "V/m", 3)}</span>
              <span>{quantity === "V" ? range.lo.toFixed(2) + " V" : siFormat(range.lo, "V/m", 3)}</span>
            </div>
          </div>
        </div>
      )}
      {g && st.result && (
        <div class="status-line muted">
          {g.nx}×{g.ny} grid · sampled in {st.result.elapsedMs.toFixed(0)} ms total
        </div>
      )}
    </div>
  );
}

function meshPath(xy: Float64Array, tris: Int32Array): string {
  const parts: string[] = [];
  for (let t = 0; t < tris.length; t += 3) {
    const i0 = tris[t]! * 2;
    const i1 = tris[t + 1]! * 2;
    const i2 = tris[t + 2]! * 2;
    parts.push(`M ${xy[i0]} ${xy[i0 + 1]} L ${xy[i1]} ${xy[i1 + 1]} L ${xy[i2]} ${xy[i2 + 1]} Z`);
  }
  return parts.join(" ");
}
